import React from "react";
import { Link } from "react-router-dom";
import { Instagram, Linkedin, Mail, MessageCircle, ArrowUpRight } from "lucide-react";
import { CONTACT_INFO, PULSE_TESTIDS, whatsappHref } from "@/lib/pulseConstants";
import { handlePulseBriefClick } from "@/lib/pulseBriefCta";
import PulseWordmark from "@/components/shared/PulseWordmark";
import {
    openCookiePreferences,
    trackContactEmail,
    trackContactWhatsApp,
} from "@/lib/analytics";

export default function SiteFooter() {
    return (
        <footer
            data-testid={PULSE_TESTIDS.footer.section}
            className="relative z-10 border-t border-white/10 bg-black text-white"
        >
            <div className="container-editorial py-16 md:py-20">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
                    <div className="md:col-span-5">
                        <div className="eyebrow mb-4">// Reform Pulse</div>
                        <h3 className="display-2 text-white">
                            Make your next brand moment <span className="text-[#FF2A2A]">impossible to ignore.</span>
                        </h3>
                        <Link
                            to="/contact"
                            onClick={handlePulseBriefClick}
                            data-testid="footer-primary-cta"
                            className="inline-flex items-center gap-2 mt-8 h-12 px-6 rounded-full bg-[#FF2A2A] text-black font-semibold hover:bg-[#ff4747] transition-colors group"
                        >
                            Start Your Pulse Brief
                            <ArrowUpRight size={18} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </Link>
                    </div>

                    <div className="md:col-span-3">
                        <div className="eyebrow mb-4">Navigate</div>
                        <ul className="space-y-2 text-white/70">
                            <li><Link to="/" className="hover:text-white">Home</Link></li>
                            <li><Link to="/services" className="hover:text-white">Services</Link></li>
                            <li><Link to="/work" className="hover:text-white">Work</Link></li>
                            <li><Link to="/about" className="hover:text-white">About</Link></li>
                            <li><Link to="/contact" className="hover:text-white">Contact</Link></li>
                        </ul>
                    </div>

                    <div className="md:col-span-4">
                        <div className="eyebrow mb-4">Say Hello</div>
                        <ul className="space-y-3">
                            <li>
                                <a
                                    href={`mailto:${CONTACT_INFO.email}`}
                                    className="inline-flex items-center gap-2 text-white/85 hover:text-white"
                                    data-testid="footer-email-link"
                                    onClick={() => trackContactEmail({ placement: "footer", source_page: "footer" })}
                                >
                                    <Mail size={16} /> {CONTACT_INFO.email}
                                </a>
                            </li>
                            <li>
                                <a
                                    href={whatsappHref("Hi REFORM Pulse — I have an event brief I’d like to discuss.")}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 text-white/85 hover:text-white"
                                    data-testid="footer-whatsapp-link"
                                    onClick={() => trackContactWhatsApp({ placement: "footer", source_page: "footer" })}
                                >
                                    <MessageCircle size={16} /> WhatsApp – {CONTACT_INFO.whatsapp}
                                </a>
                            </li>
                            <li>
                                <a
                                    href={CONTACT_INFO.instagramUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 text-white/85 hover:text-white"
                                    data-testid="footer-instagram-link"
                                >
                                    <Instagram size={16} /> {CONTACT_INFO.instagram}
                                </a>
                            </li>
                            <li>
                                <a
                                    href={CONTACT_INFO.linkedinUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 text-white/85 hover:text-white"
                                    data-testid="footer-linkedin-link"
                                >
                                    <Linkedin size={16} /> {CONTACT_INFO.linkedin}
                                </a>
                            </li>
                        </ul>
                    </div>
                </div>

                <div className="rule my-12" />

                <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
                    <div>
                        <PulseWordmark size="lg" showTagline={false} />
                        <p className="font-mono text-xs text-white/50 uppercase tracking-[0.22em] mt-3">
                            Part of REFORM Group &nbsp;&middot;&nbsp; {CONTACT_INFO.city}
                        </p>
                    </div>
                    <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-8 text-xs font-mono text-white/50 uppercase tracking-widest">
                        <span>© {new Date().getFullYear()} REFORM Pulse.</span>
                        <Link to="/privacy" className="hover:text-white min-h-11 py-3 inline-flex items-center" data-testid="footer-privacy-link">Privacy Policy</Link>
                        <Link to="/terms" className="hover:text-white min-h-11 py-3 inline-flex items-center" data-testid="footer-terms-link">Terms</Link>
                        <button
                            type="button"
                            onClick={openCookiePreferences}
                            className="hover:text-white min-h-11 py-3 inline-flex items-center uppercase tracking-widest bg-transparent"
                            data-testid="footer-cookie-preferences"
                        >
                            Cookie Preferences
                        </button>
                        <Link to="/admin" className="hover:text-white min-h-11 py-3 inline-flex items-center" data-testid="footer-admin-link">Admin</Link>
                    </div>
                </div>
            </div>
        </footer>
    );
}
